/* StatsStrip — small animated counter strip used between Programs and Tracks
 * on the Academy landing page. The four stats reflect real product facts
 * (no fabricated learner numbers). Counters animate on first scroll-into-view
 * via IntersectionObserver — runs once per cell so re-scrolls are silent. */
import React, { useEffect, useRef, useState } from 'react';
import { GraduationCap, Layers, Headphones, ShieldCheck } from 'lucide-react';

const STATS = [
  {
    icon: GraduationCap,
    value: 10,
    suffix: '',
    label: 'Career-aligned programs',
    sub: 'IT · Cyber · Programming · Math · Soft skills',
  },
  {
    icon: Layers,
    value: 3,
    suffix: '',
    label: 'Skill levels',
    sub: 'Beginner · Intermediate · Advanced',
  },
  {
    icon: Headphones,
    value: 24,
    suffix: '/7',
    label: 'Mentor support',
    sub: '1-on-1 guidance from working pros',
  },
  {
    icon: ShieldCheck,
    value: 100,
    suffix: '%',
    label: 'Verifiable certificates',
    sub: 'Public verification page per cert',
  },
];

function CountUp({ to, suffix = '', duration = 1100, started }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!started) return;
    let raf = 0;
    const start = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - start) / duration);
      // ease-out cubic for a soft landing
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(eased * to));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [started, to, duration]);
  return <>{n}{suffix}</>;
}

export default function StatsStrip() {
  const ref = useRef(null);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    if (!ref.current || started) return;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) setStarted(true); }),
      { threshold: 0.4 },
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [started]);

  return (
    <section
      ref={ref}
      data-testid="academy-stats-strip"
      className="relative bg-[#0e0e10] text-white py-12 md:py-14 border-t border-b border-white/5"
    >
      {/* faint gold rule above */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#c9a227]/40 to-transparent" />
      <div className="zh-container">
        <ul className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-8 md:divide-x md:divide-white/10">
          {STATS.map((s, i) => {
            const Icon = s.icon;
            return (
              <li
                key={s.label}
                data-testid={`academy-stat-${i}`}
                className="px-1 md:px-6 first:md:pl-0 last:md:pr-0 text-center md:text-left"
              >
                <div className="flex items-center justify-center md:justify-start gap-2 mb-2">
                  <span className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-[#c9a227]/15 text-[#e6c35a]">
                    <Icon className="h-4 w-4" strokeWidth={2.2} />
                  </span>
                  <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#e6c35a]">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                </div>
                <div className="text-3xl md:text-4xl lg:text-5xl font-extrabold leading-none bg-gradient-to-r from-[#e6c35a] via-[#c9a227] to-[#a8871a] bg-clip-text text-transparent tabular-nums">
                  <CountUp to={s.value} suffix={s.suffix} started={started} />
                </div>
                <div className="mt-3 text-[13px] font-bold text-white/95">{s.label}</div>
                <div className="text-[11.5px] text-white/55 mt-0.5">{s.sub}</div>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
